import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate, statusBadgeClass, statusLabel } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  FileText,
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  Send,
  Calendar,
  BookOpen,
  RefreshCw,
  Trash2 as TrashIcon,
  PlusCircle,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';

function JournalRow({ journal, onView, onVerify, onPost }) {
  return (
    <tr>
      <td>
        <span className="font-medium text-gray-900 dark:text-white">{journal.journal_number || journal.number || '-'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatDate(journal.date || journal.journal_date)}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200 truncate max-w-[200px]">{journal.description || '-'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-500 dark:text-gray-400">{journal.source || journal.source_module || '-'}</span>
      </td>
      <td className="text-right">
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatCurrency(journal.debit_total || journal.total_debit)}</span>
      </td>
      <td className="text-right">
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatCurrency(journal.credit_total || journal.total_credit)}</span>
      </td>
      <td>
        <span className={`badge ${statusBadgeClass(journal.status)}`}>{statusLabel(journal.status)}</span>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <button onClick={() => onView(journal)} className="p-1.5 text-primary-900 hover:bg-primary-50 dark:hover:bg-primary-950/50 rounded" title="Detail">
            <FileText className="h-4 w-4" />
          </button>
          {journal.status === 'draft' && (
            <>
              <button onClick={() => onVerify(journal)} className="p-1.5 text-warning-600 hover:bg-warning-50 dark:hover:bg-warning-900/20 rounded" title="Verifikasi">
                <Check className="h-4 w-4" />
              </button>
            </>
          )}
          {journal.status === 'verified' && (
            <button onClick={() => onPost(journal)} className="p-1.5 text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20 rounded" title="Posting">
              <Send className="h-4 w-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function JournalLine({ line, index, onRemove }) {
  return (
    <tr>
      <td>
        <span className="text-sm font-medium text-gray-900 dark:text-white">{index + 1}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">
          {line.account?.code || line.account_code || '-'} - {line.account?.name || line.account_name || '-'}
        </span>
      </td>
      <td>
        <span className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">{line.description || '-'}</span>
      </td>
      <td className="text-right">
        <span className="text-sm text-primary-900 dark:text-primary-400">{line.debit > 0 ? formatCurrency(line.debit) : '-'}</span>
      </td>
      <td className="text-right">
        <span className="text-sm text-danger-700 dark:text-danger-400">{line.credit > 0 ? formatCurrency(line.credit) : '-'}</span>
      </td>
      <td>
        {onRemove && (
          <button type="button" onClick={() => onRemove(index)} className="p-1.5 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded">
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      </td>
    </tr>
  );
}

export default function JournalsPage() {
  const { hasPermission } = useAuth();
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ date_from: '', date_to: '', status: '', source: '' });

  // Detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedJournal, setSelectedJournal] = useState(null);
  const [journalLines, setJournalLines] = useState([]);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState({ date: '', description: '' });
  const [lines, setLines] = useState([
    { account_id: '', description: '', debit: 0, credit: 0 },
    { account_id: '', description: '', debit: 0, credit: 0 },
  ]);
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchJournals();
  }, [pagination.page]);

  const fetchJournals = async () => {
    setLoading(true);
    try {
      const params = queryParams({ page: pagination.page, limit: pagination.limit, ...filters });
      const res = await api.get('/journals', { params });
      if (res.data.success) {
        const data = res.data.data;
        setJournals(data?.journals || data?.data || []);
        setPagination((prev) => ({
          ...prev,
          total: data?.total || data?.pagination?.total || 0,
          totalPages: data?.totalPages || data?.pagination?.totalPages || Math.ceil((data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await api.get('/accounts', { params: { limit: 200 } });
      if (res.data.success) {
        const data = res.data.data;
        setAccounts(data?.accounts || data?.data || []);
      }
    } catch {
      // non-critical
    }
  };

  const handleFilter = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchJournals();
  };

  const resetFilters = () => {
    setFilters({ date_from: '', date_to: '', status: '', source: '' });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const openDetail = async (journal) => {
    setSelectedJournal(journal);
    setJournalLines([]);
    try {
      const res = await api.get(`/journals/${journal.id}`);
      if (res.data.success) {
        const data = res.data.data;
        setJournalLines(data?.lines || data?.entries || []);
      }
    } catch {
      // Try fallback - maybe lines are already in journal object
      setJournalLines(journal.lines || journal.entries || []);
    }
    setShowDetailModal(true);
  };

  const handleVerify = async (journal) => {
    if (!confirm(`Verifikasi jurnal ${journal.journal_number || journal.number}?`)) return;
    try {
      const res = await api.put(`/journals/${journal.id}/verify`);
      if (res.data.success) {
        toast.success('Jurnal berhasil diverifikasi');
        fetchJournals();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handlePost = async (journal) => {
    if (!confirm(`Posting jurnal ${journal.journal_number || journal.number}? Aksi ini tidak dapat dibatalkan.`)) return;
    try {
      const res = await api.put(`/journals/${journal.id}/post`);
      if (res.data.success) {
        toast.success('Jurnal berhasil diposting');
        fetchJournals();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleAddLine = () => {
    setLines([...lines, { account_id: '', description: '', debit: 0, credit: 0 }]);
  };

  const handleRemoveLine = (index) => {
    if (lines.length <= 2) {
      toast.error('Jurnal minimal memiliki 2 baris');
      return;
    }
    setLines(lines.filter((_, i) => i !== index));
  };

  const handleLineChange = (index, field, value) => {
    const updated = [...lines];
    updated[index] = { ...updated[index], [field]: value };
    setLines(updated);
  };

  const totalDebit = lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
  const totalCredit = lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 1;

  const handleCreateJournal = async (e) => {
    e.preventDefault();
    if (!isBalanced) {
      toast.error('Total debit dan kredit tidak seimbang');
      return;
    }
    const validLines = lines.filter((l) => l.account_id && ((parseFloat(l.debit) || 0) > 0 || (parseFloat(l.credit) || 0) > 0));
    if (validLines.length < 2) {
      toast.error('Minimal 2 baris jurnal diperlukan');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/journals', {
        ...form,
        lines: validLines.map((l) => ({
          account_id: l.account_id,
          description: l.description,
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
        })),
      });
      if (res.data.success) {
        toast.success('Jurnal berhasil dibuat');
        setShowCreateModal(false);
        resetForm();
        fetchJournals();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setForm({ date: '', description: '' });
    setLines([
      { account_id: '', description: '', debit: 0, credit: 0 },
      { account_id: '', description: '', debit: 0, credit: 0 },
    ]);
  };

  const openCreateModal = () => {
    resetForm();
    fetchAccounts();
    setShowCreateModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Jurnal</h1>
          <p className="page-subtitle">Kelola jurnal akuntansi</p>
        </div>
        {hasPermission('create_journals') && (
          <button onClick={openCreateModal} className="btn-primary">
            <Plus className="h-4 w-4 mr-2" /> Jurnal Baru
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input type="date" className="input pl-9" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} placeholder="Dari tanggal" />
            </div>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input type="date" className="input pl-9" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} placeholder="Sampai tanggal" />
            </div>
            <div>
              <select className="input" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                <option value="">Semua Status</option>
                <option value="draft">Draf</option>
                <option value="verified">Terverifikasi</option>
                <option value="posted">Diposting</option>
                <option value="cancelled">Dibatalkan</option>
              </select>
            </div>
            <div>
              <select className="input" value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })}>
                <option value="">Semua Sumber</option>
                <option value="manual">Manual</option>
                <option value="purchase">Pembelian</option>
                <option value="sales">Penjualan</option>
                <option value="cash_bank">Kas & Bank</option>
                <option value="other">Lainnya</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={handleFilter} className="btn-primary btn-sm flex-1">
                <Search className="h-4 w-4 mr-1" /> Filter
              </button>
              <button onClick={resetFilters} className="btn-secondary btn-sm">
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-32" />
                  <div className="skeleton h-4 w-48" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-32 ml-auto" />
                  <div className="skeleton h-4 w-32 ml-auto" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-20" />
                </div>
              ))}
            </div>
          </div>
        ) : journals.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Jurnal</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada jurnal yang dibuat.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>No. Jurnal</th>
                    <th>Tanggal</th>
                    <th>Deskripsi</th>
                    <th>Sumber</th>
                    <th className="text-right">Total Debit</th>
                    <th className="text-right">Total Kredit</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {journals.map((j) => (
                    <JournalRow key={j.id} journal={j} onView={openDetail} onVerify={handleVerify} onPost={handlePost} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="btn btn-sm btn-outline">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">Halaman {pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))} disabled={pagination.page >= pagination.totalPages} className="btn btn-sm btn-outline">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedJournal && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">
                Detail Jurnal: {selectedJournal.journal_number || selectedJournal.number}
              </h3>
              <button onClick={() => setShowDetailModal(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Tanggal</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(selectedJournal.date || selectedJournal.journal_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Sumber</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedJournal.source || selectedJournal.source_module || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                  <span className={`badge ${statusBadgeClass(selectedJournal.status)}`}>{statusLabel(selectedJournal.status)}</span>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(selectedJournal.debit_total || selectedJournal.total_debit)}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Deskripsi</p>
                <p className="text-sm text-gray-700 dark:text-gray-200">{selectedJournal.description || '-'}</p>
              </div>

              {/* Lines Table */}
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Akun</th>
                      <th>Deskripsi</th>
                      <th className="text-right">Debit</th>
                      <th className="text-right">Kredit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {journalLines.length > 0 ? (
                      journalLines.map((line, i) => <JournalLine key={i} line={line} index={i} />)
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-gray-500">
                          Tidak ada detail baris jurnal.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold bg-gray-50 dark:bg-gray-700/50">
                      <td colSpan={3} className="text-right text-gray-900 dark:text-white">Total</td>
                      <td className="text-right text-primary-900 dark:text-primary-400">
                        {formatCurrency(journalLines.reduce((s, l) => s + (l.debit || 0), 0))}
                      </td>
                      <td className="text-right text-danger-700 dark:text-danger-400">
                        {formatCurrency(journalLines.reduce((s, l) => s + (l.credit || 0), 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                {selectedJournal.status === 'draft' && (
                  <button onClick={() => { handleVerify(selectedJournal); setShowDetailModal(false); }} className="btn-warning btn-sm">
                    <Check className="h-4 w-4 mr-1" /> Verifikasi
                  </button>
                )}
                {selectedJournal.status === 'verified' && (
                  <button onClick={() => { handlePost(selectedJournal); setShowDetailModal(false); }} className="btn-success btn-sm">
                    <Send className="h-4 w-4 mr-1" /> Posting
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Journal Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Buat Jurnal Baru</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleCreateJournal}>
              <div className="p-6 space-y-4">
                {/* Journal Header */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Tanggal <span className="text-danger-600">*</span></label>
                    <input type="date" className="input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
                  </div>
                  <div>
                    <label className="label">Deskripsi <span className="text-danger-600">*</span></label>
                    <input type="text" className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required placeholder="Deskripsi jurnal" />
                  </div>
                </div>

                {/* Lines */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Baris Jurnal</label>
                    <button type="button" onClick={handleAddLine} className="btn-primary btn-sm">
                      <PlusCircle className="h-4 w-4 mr-1" /> Tambah Baris
                    </button>
                  </div>
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Akun <span className="text-danger-600">*</span></th>
                          <th>Deskripsi</th>
                          <th className="text-right">Debit</th>
                          <th className="text-right">Kredit</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {lines.map((line, i) => (
                          <tr key={i}>
                            <td className="text-sm text-gray-500">{i + 1}</td>
                            <td>
                              <select
                                className="input text-sm"
                                value={line.account_id}
                                onChange={(e) => handleLineChange(i, 'account_id', e.target.value)}
                                required
                              >
                                <option value="">Pilih Akun</option>
                                {accounts.map((acc) => (
                                  <option key={acc.id} value={acc.id}>
                                    {acc.code || acc.account_code} - {acc.name || acc.account_name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input
                                type="text"
                                className="input text-sm"
                                value={line.description}
                                onChange={(e) => handleLineChange(i, 'description', e.target.value)}
                                placeholder="Deskripsi baris"
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="input text-sm text-right"
                                value={line.debit || ''}
                                onChange={(e) => {
                                  handleLineChange(i, 'debit', e.target.value);
                                  if (e.target.value && parseFloat(e.target.value) > 0) handleLineChange(i, 'credit', 0);
                                }}
                                min="0"
                                placeholder="0"
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="input text-sm text-right"
                                value={line.credit || ''}
                                onChange={(e) => {
                                  handleLineChange(i, 'credit', e.target.value);
                                  if (e.target.value && parseFloat(e.target.value) > 0) handleLineChange(i, 'debit', 0);
                                }}
                                min="0"
                                placeholder="0"
                              />
                            </td>
                            <td>
                              <button type="button" onClick={() => handleRemoveLine(i)} className="p-1.5 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded">
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="font-semibold bg-gray-50 dark:bg-gray-700/50">
                          <td colSpan={3} className="text-right text-gray-900 dark:text-white">Total</td>
                          <td className="text-right text-primary-900 dark:text-primary-400">{formatCurrency(totalDebit)}</td>
                          <td className="text-right text-danger-700 dark:text-danger-400">{formatCurrency(totalCredit)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {!isBalanced && totalDebit > 0 && totalCredit > 0 && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-danger-600 dark:text-danger-400">
                      <AlertCircle className="h-4 w-4" />
                      <span>Total debit ({formatCurrency(totalDebit)}) tidak sama dengan total kredit ({formatCurrency(totalCredit)})</span>
                    </div>
                  )}
                  {isBalanced && totalDebit > 0 && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-success-600 dark:text-success-400">
                      <CheckCircle2 className="h-4 w-4" />
                      <span>Balance: {formatCurrency(totalDebit)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3 justify-end">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary">Batal</button>
                <button type="submit" disabled={saving || !isBalanced} className="btn-primary">
                  {saving ? 'Menyimpan...' : 'Simpan Jurnal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
